import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { KeyRound, MapPin, SlidersHorizontal, Plug, Save, Wand2, Download, Webhook, Copy, Check, Lock, Car } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";

function Section({ icon: Icon, title, desc, children }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900/60 rounded-md p-6">
      <div className="flex items-center gap-2.5 mb-1">
        <Icon className="h-4 w-4 text-blue-400" />
        <h2 className="font-heading font-bold text-base">{title}</h2>
      </div>
      {desc && <p className="text-xs text-zinc-500 mb-4">{desc}</p>}
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-wider text-zinc-400">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-zinc-600">{hint}</p>}
    </div>
  );
}

const inputCls = "bg-zinc-950 border-zinc-700 focus:ring-blue-500 focus:border-blue-500 rounded";

export default function Settings() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.getSettings });
  const [form, setForm] = useState(null);
  const [secrets, setSecrets] = useState({ authPassword: "", apiKey: "", webhookSecret: "" });
  const [importId, setImportId] = useState("");
  const [copied, setCopied] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "" });
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/transferz`;

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      toast.success("Webhook URL kopyalandı");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  useEffect(() => {
    if (settings && !form) {
      setForm({
        environment: settings.environment,
        authEmail: settings.authEmail || "",
        driverCode: settings.driverCode || "",
        liveLocationEnabled: settings.liveLocationEnabled,
        staticLatitude: settings.staticLatitude ?? "",
        staticLongitude: settings.staticLongitude ?? "",
        autoAssign: settings.autoAssign ?? true,
        driverName: settings.driverName || "",
        driverPhone: settings.driverPhone || "",
        notifyDriverSms: settings.notifyDriverSms ?? false,
        vehiclePlate: settings.vehiclePlate || "",
        vehicleMake: settings.vehicleMake || "",
        vehicleModel: settings.vehicleModel || "",
        vehicleSeries: settings.vehicleSeries || "",
        rules: { ...settings.rules },
      });
    }
  }, [settings, form]);

  const saveMut = useMutation({
    mutationFn: (payload) => api.updateSettings(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings"] });
      setSecrets({ authPassword: "", apiKey: "" });
      toast.success("Ayarlar kaydedildi");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kaydedilemedi"),
  });

  const genMut = useMutation({
    mutationFn: () => api.generateKey({}),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success(`API anahtarı oluşturuldu: ${d.apiKeyPreview}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Anahtar oluşturulamadı"),
  });

  const testMut = useMutation({
    mutationFn: api.testConnection,
    onSuccess: (d) => toast.success(`Bağlantı başarılı · ${d.totalJourneys} rezervasyon`),
    onError: (e) => toast.error(e?.response?.data?.detail || "Bağlantı başarısız"),
  });

  const importMut = useMutation({
    mutationFn: () => api.importJourney(importId.trim()),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["journeys"] });
      setImportId("");
      toast.success(`İçe aktarıldı: ${d.codes || ""}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "İçe aktarılamadı"),
  });

  const pwMut = useMutation({
    mutationFn: () => api.changePassword(pwForm.current, pwForm.next),
    onSuccess: () => {
      setPwForm({ current: "", next: "" });
      toast.success("Şifre güncellendi");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Şifre değiştirilemedi"),
  });

  if (!form) return <div className="p-8 text-zinc-500 font-mono">Yükleniyor…</div>;

  const setRule = (k, v) => setForm((f) => ({ ...f, rules: { ...f.rules, [k]: v } }));

  const handleSave = () => {
    const payload = {
      environment: form.environment,
      authEmail: form.authEmail,
      driverCode: form.driverCode,
      liveLocationEnabled: form.liveLocationEnabled,
      staticLatitude: form.staticLatitude === "" ? null : parseFloat(form.staticLatitude),
      staticLongitude: form.staticLongitude === "" ? null : parseFloat(form.staticLongitude),
      autoAssign: form.autoAssign,
      driverName: form.driverName,
      driverPhone: form.driverPhone,
      notifyDriverSms: form.notifyDriverSms,
      vehiclePlate: form.vehiclePlate,
      vehicleMake: form.vehicleMake,
      vehicleModel: form.vehicleModel,
      vehicleSeries: form.vehicleSeries,
      rules: {
        underwayMinutesBefore: parseInt(form.rules.underwayMinutesBefore, 10),
        arrivedOffsetMinutes: parseInt(form.rules.arrivedOffsetMinutes, 10),
        pickedUpMinutesAfter: parseInt(form.rules.pickedUpMinutesAfter, 10),
        completedMode: form.rules.completedMode,
        completedOffsetMinutes: parseInt(form.rules.completedOffsetMinutes, 10),
        completedFixedMinutes: parseInt(form.rules.completedFixedMinutes, 10),
      },
    };
    if (secrets.authPassword) payload.authPassword = secrets.authPassword;
    if (secrets.apiKey) payload.apiKey = secrets.apiKey;
    if (secrets.webhookSecret) payload.webhookSecret = secrets.webhookSecret;
    saveMut.mutate(payload);
  };

  const r = form.rules;
  const summary = `Alım saatinden ${r.underwayMinutesBefore} dk önce işi başlat → alımdan ${r.arrivedOffsetMinutes} dk sonra "ulaştım" → alımdan ${r.pickedUpMinutesAfter} dk sonra "misafiri aldım" → ${r.completedMode === "duration" ? `tahmini süre + ${r.completedOffsetMinutes} dk sonra` : `${r.completedFixedMinutes} dk sonra`} "misafiri bıraktım".`;

  return (
    <div className="p-6 lg:p-8 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Ayarlar</h1>
          <p className="text-sm text-zinc-500 mt-1">API bağlantısı ve otomasyon kuralları</p>
        </div>
        <Button
          data-testid="save-settings-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white active:scale-95"
          onClick={handleSave}
          disabled={saveMut.isPending}
        >
          <Save className="h-4 w-4 mr-2" /> Kaydet
        </Button>
      </div>

      <div className="space-y-5">
        <Section icon={KeyRound} title="TransferZ API Ayarları" desc="Kimlik bilgilerinizle otomatik API anahtarı üretin ya da hazır anahtarınızı yapıştırın.">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Ortam">
              <Select value={form.environment} onValueChange={(v) => setForm({ ...form, environment: v })}>
                <SelectTrigger data-testid="env-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="staging">Staging (Test)</SelectItem>
                  <SelectItem value="live">Production (Canlı)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Sürücü Bağlantı Durumu">
              <div className="h-10 flex items-center px-3 rounded border border-zinc-700 bg-zinc-950 text-sm font-mono">
                {settings?.apiKeySet ? (
                  <span className="text-emerald-400">{settings.apiKeyPreview}</span>
                ) : (
                  <span className="text-zinc-600">Anahtar yok</span>
                )}
              </div>
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="E-posta">
              <Input data-testid="email-input" className={inputCls} value={form.authEmail}
                onChange={(e) => setForm({ ...form, authEmail: e.target.value })} placeholder="user@domain.com" />
            </Field>
            <Field label="Şifre">
              <Input data-testid="password-input" type="password" className={inputCls} value={secrets.authPassword}
                onChange={(e) => setSecrets({ ...secrets, authPassword: e.target.value })}
                placeholder={settings?.passwordSet ? "•••••••• (kayıtlı)" : "Şifre"} />
              {settings?.passwordSet && (
                <p className="text-[11px] text-emerald-400 flex items-center gap-1">
                  <Check className="h-3 w-3" /> Kayıtlı — değiştirmek için yeni şifre girin
                </p>
              )}
            </Field>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button data-testid="generate-key-btn" variant="outline"
              className="border-zinc-700 bg-transparent hover:bg-zinc-800"
              onClick={() => {
                if (secrets.authPassword || form.authEmail) {
                  saveMut.mutate({
                    authEmail: form.authEmail, environment: form.environment,
                    ...(secrets.authPassword ? { authPassword: secrets.authPassword } : {}),
                  }, { onSuccess: () => genMut.mutate() });
                } else {
                  genMut.mutate();
                }
              }}
              disabled={genMut.isPending}>
              <Wand2 className="h-4 w-4 mr-2" /> API Anahtarı Oluştur
            </Button>
            <Button data-testid="test-connection-btn" variant="outline"
              className="border-zinc-700 bg-transparent hover:bg-zinc-800"
              onClick={() => testMut.mutate()} disabled={testMut.isPending}>
              <Plug className="h-4 w-4 mr-2" /> Bağlantıyı Test Et
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Ya da Hazır API Anahtarı</Label>
            <div className="flex gap-2">
              <Input data-testid="apikey-input" className={inputCls} value={secrets.apiKey}
                onChange={(e) => setSecrets({ ...secrets, apiKey: e.target.value })}
                placeholder={settings?.apiKeySet ? "Kayıtlı — değiştirmek için yeni anahtar yapıştırın" : "fmJbaeQ02Mns22BW3qQnFQyo"} />
              <Button data-testid="save-apikey-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white shrink-0 active:scale-95"
                onClick={() => saveMut.mutate({ apiKey: secrets.apiKey, environment: form.environment })}
                disabled={!secrets.apiKey || saveMut.isPending}>
                <Save className="h-4 w-4 mr-2" /> Anahtarı Kaydet
              </Button>
            </div>
            {settings?.apiKeySet ? (
              <p data-testid="apikey-saved-status" className="text-[11px] text-emerald-400 flex items-center gap-1">
                <Check className="h-3 w-3" /> API anahtarı kayıtlı ve kullanımda: {settings.apiKeyPreview}
              </p>
            ) : (
              <p className="text-[11px] text-zinc-600">X-API-Key değerini yapıştırıp "Anahtarı Kaydet"e basın.</p>
            )}
          </div>
        </Section>

        <Section icon={MapPin} title="Sürücü & Konum" desc="Canlı takip için gönderilecek sabit konum ve sürücü kodu.">
          <Field label="Sürücü Kodu (opsiyonel)" hint="Sadece bu sürücüye ait rezervasyonları çekmek için.">
            <Input data-testid="driver-code-input" className={inputCls} value={form.driverCode}
              onChange={(e) => setForm({ ...form, driverCode: e.target.value })} placeholder="936QBY032" />
          </Field>
          <div className="flex items-center justify-between border border-zinc-800 rounded px-4 py-3">
            <div>
              <div className="text-sm text-zinc-200">Canlı Konum Gönderimi</div>
              <div className="text-[11px] text-zinc-500">Aktif yolculuklarda ~30 sn'de bir konum gönderilir.</div>
            </div>
            <Switch data-testid="live-location-switch" checked={form.liveLocationEnabled}
              onCheckedChange={(v) => setForm({ ...form, liveLocationEnabled: v })} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Enlem (Latitude)">
              <Input data-testid="lat-input" className={inputCls} value={form.staticLatitude}
                onChange={(e) => setForm({ ...form, staticLatitude: e.target.value })} placeholder="52.3095" />
            </Field>
            <Field label="Boylam (Longitude)">
              <Input data-testid="lng-input" className={inputCls} value={form.staticLongitude}
                onChange={(e) => setForm({ ...form, staticLongitude: e.target.value })} placeholder="4.7634" />
            </Field>
          </div>
        </Section>

        <Section icon={Car} title="Sürücü & Araç (Otomatik Atama)" desc="DIRECT ASSIGNMENT: iş başlatılmadan önce (CONFIRMED → PLANNED) otomatik atanacak sürücü ve araç bilgileri. Önce sürücü, sonra araç atanır.">
          <div className="flex items-center justify-between border border-zinc-800 rounded px-4 py-3">
            <div>
              <div className="text-sm text-zinc-200">Otomatik Sürücü/Araç Ataması</div>
              <div className="text-[11px] text-zinc-500">CONFIRMED işlere iş başlatmadan önce otomatik sürücü + araç atar.</div>
            </div>
            <Switch data-testid="auto-assign-switch" checked={form.autoAssign}
              onCheckedChange={(v) => setForm({ ...form, autoAssign: v })} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Sürücü Adı">
              <Input data-testid="driver-name-input" className={inputCls} value={form.driverName}
                onChange={(e) => setForm({ ...form, driverName: e.target.value })} placeholder="Ahmet Yılmaz" />
            </Field>
            <Field label="Sürücü Telefonu" hint="Ülke koduyla, ör. +905551234567">
              <Input data-testid="driver-phone-input" className={inputCls} value={form.driverPhone}
                onChange={(e) => setForm({ ...form, driverPhone: e.target.value })} placeholder="+905551234567" />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="Plaka">
              <Input data-testid="vehicle-plate-input" className={inputCls} value={form.vehiclePlate}
                onChange={(e) => setForm({ ...form, vehiclePlate: e.target.value })} placeholder="34 ABC 123" />
            </Field>
            <Field label="Marka">
              <Input data-testid="vehicle-make-input" className={inputCls} value={form.vehicleMake}
                onChange={(e) => setForm({ ...form, vehicleMake: e.target.value })} placeholder="Mercedes" />
            </Field>
            <Field label="Model">
              <Input data-testid="vehicle-model-input" className={inputCls} value={form.vehicleModel}
                onChange={(e) => setForm({ ...form, vehicleModel: e.target.value })} placeholder="Vito" />
            </Field>
            <Field label="Seri">
              <Input data-testid="vehicle-series-input" className={inputCls} value={form.vehicleSeries}
                onChange={(e) => setForm({ ...form, vehicleSeries: e.target.value })} placeholder="Tourer" />
            </Field>
          </div>
          <div className="flex items-center justify-between border border-zinc-800 rounded px-4 py-3">
            <div className="text-sm text-zinc-200">Sürücüye SMS gönder</div>
            <Switch data-testid="notify-sms-switch" checked={form.notifyDriverSms}
              onCheckedChange={(v) => setForm({ ...form, notifyDriverSms: v })} />
          </div>
        </Section>

        <Section icon={Webhook} title="Webhook (Anlık Bildirim)" desc="Bu URL'yi TransferZ'e verin; yeni işler ve durum değişiklikleri panele anında düşer.">
          <Field label="Webhook URL" hint="TransferZ partner/destinasyon yöneticinize bu adresi iletin.">
            <div className="flex gap-2">
              <Input data-testid="webhook-url" readOnly className={`${inputCls} font-mono text-xs`} value={webhookUrl} />
              <Button data-testid="copy-webhook-btn" variant="outline"
                className="border-zinc-700 bg-transparent hover:bg-zinc-800 shrink-0"
                onClick={copyWebhook}>
                {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </Field>
          <Field label="Webhook Gizli Anahtarı (HMAC)"
            hint={settings?.webhookSecretSet
              ? "Ayarlı. İmza doğrulaması aktif. Değiştirmek için doldurun."
              : "TransferZ'in verdiği secret'ı girin; girilmezse imza doğrulaması yapılmaz (tüm istekler kabul edilir)."}>
            <Input data-testid="webhook-secret-input" type="password" className={inputCls} value={secrets.webhookSecret}
              onChange={(e) => setSecrets({ ...secrets, webhookSecret: e.target.value })}
              placeholder={settings?.webhookSecretSet ? "••••••••" : "6c13b77184bf692f66eb1568b2d83577"} />
          </Field>
        </Section>

        <Section icon={SlidersHorizontal} title="Otomasyon Kuralları" desc="Adımların hangi zamanlamayla otomatik yapılacağını belirleyin.">
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="İşi Başlat (dk önce)" hint="Alım saatinden önce">
              <Input data-testid="rule-underway" type="number" className={inputCls} value={r.underwayMinutesBefore}
                onChange={(e) => setRule("underwayMinutesBefore", e.target.value)} />
            </Field>
            <Field label="Ulaştım (dk offset)" hint="Alım saatine göre">
              <Input data-testid="rule-arrived" type="number" className={inputCls} value={r.arrivedOffsetMinutes}
                onChange={(e) => setRule("arrivedOffsetMinutes", e.target.value)} />
            </Field>
            <Field label="Misafiri Aldım (dk sonra)" hint="Alım saatinden sonra">
              <Input data-testid="rule-pickedup" type="number" className={inputCls} value={r.pickedUpMinutesAfter}
                onChange={(e) => setRule("pickedUpMinutesAfter", e.target.value)} />
            </Field>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Tamamlama Yöntemi">
              <Select value={r.completedMode} onValueChange={(v) => setRule("completedMode", v)}>
                <SelectTrigger data-testid="rule-complete-mode" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="duration">Tahmini süreye göre</SelectItem>
                  <SelectItem value="fixed">Sabit süre</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {r.completedMode === "duration" ? (
              <Field label="Ek Süre (dk)" hint="Tahmini süreye eklenir">
                <Input data-testid="rule-complete-offset" type="number" className={inputCls} value={r.completedOffsetMinutes}
                  onChange={(e) => setRule("completedOffsetMinutes", e.target.value)} />
              </Field>
            ) : (
              <Field label="Sabit Süre (dk)" hint="Misafiri aldıktan sonra">
                <Input data-testid="rule-complete-fixed" type="number" className={inputCls} value={r.completedFixedMinutes}
                  onChange={(e) => setRule("completedFixedMinutes", e.target.value)} />
              </Field>
            )}
          </div>
          <div className="rounded border border-blue-900/40 bg-blue-950/20 p-3 text-[12px] text-blue-200 font-mono leading-relaxed">
            {summary}
          </div>
        </Section>

        <Section icon={Download} title="Rezervasyon İçe Aktarma & Senkronizasyon" desc="Senkronizasyon, şirketinize atanmış (kabul edilmiş) tüm işleri tarih sınırı olmadan çeker. Belirli bir işi hemen çekmek için kod/ID kullanın.">
          <Field label="Journey/Booking Kodu veya ID ile İçe Aktar"
            hint="Rezervasyon kodunu (ör. 52MSZM-1) veya sayısal ID'yi girin. Bulunamazsa iş henüz şirketinize atanmamış olabilir.">
            <div className="flex gap-2">
              <Input data-testid="import-id-input" className={inputCls} value={importId}
                onChange={(e) => setImportId(e.target.value)} placeholder="52MSZM-1 veya 1655414" />
              <Button data-testid="import-btn" variant="outline"
                className="border-zinc-700 bg-transparent hover:bg-zinc-800 shrink-0"
                onClick={() => importMut.mutate()} disabled={!importId.trim() || importMut.isPending}>
                <Download className="h-4 w-4 mr-2" /> Aktar
              </Button>
            </div>
          </Field>
        </Section>

        <Section icon={Lock} title="Hesap / Şifre Değiştir" desc="Panele giriş şifrenizi buradan güncelleyin.">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Mevcut Şifre">
              <Input data-testid="current-password-input" type="password" className={inputCls} value={pwForm.current}
                onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} placeholder="••••••••" />
            </Field>
            <Field label="Yeni Şifre" hint="En az 6 karakter">
              <Input data-testid="new-password-input" type="password" className={inputCls} value={pwForm.next}
                onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} placeholder="••••••••" />
            </Field>
          </div>
          <Button data-testid="change-password-btn" variant="outline"
            className="border-zinc-700 bg-transparent hover:bg-zinc-800"
            onClick={() => pwMut.mutate()}
            disabled={!pwForm.current || !pwForm.next || pwMut.isPending}>
            <Lock className="h-4 w-4 mr-2" /> Şifreyi Güncelle
          </Button>
        </Section>
      </div>
    </div>
  );
}
